import React, { useState, useEffect } from 'react'
import {
  Box,
  Typography,
  Button,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  CircularProgress,
  IconButton,
  Tooltip,
} from '@mui/material'
import { DataGrid } from '@mui/x-data-grid'
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  ContentCopy as DuplicateIcon,
  Visibility as ViewIcon,
} from '@mui/icons-material'
import { domainsAPI } from '../services/api'
import { useAuth } from '../contexts/AuthContext'

function Domains() {
  const { canManageBaseContent } = useAuth()
  const [domains, setDomains] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [dialogOpen, setDialogOpen] = useState(false)
  const [editingDomain, setEditingDomain] = useState(null)
  const [formData, setFormData] = useState({
    code: '',
    name: '',
    description: '',
  })
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    fetchDomains()
  }, [])

  const fetchDomains = async () => {
    try {
      setLoading(true)
      setError(null)
      const response = await domainsAPI.getAll()
      setDomains(response.data || [])
    } catch (err) {
      console.error('Error fetching domains:', err)
      setError('Failed to load domains. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  const handleOpenDialog = (domain = null) => {
    if (domain) {
      setEditingDomain(domain)
      setFormData({
        code: domain.code || '',
        name: domain.name || '',
        description: domain.description || '',
      })
    } else {
      setEditingDomain(null)
      setFormData({
        code: '',
        name: '',
        description: '',
      })
    }
    setDialogOpen(true)
  }

  const handleCloseDialog = () => {
    setDialogOpen(false)
    setEditingDomain(null)
    setFormData({
      code: '',
      name: '',
      description: '',
    })
  }

  const handleSave = async () => {
    try {
      setSaving(true)
      setError(null)
      if (editingDomain) {
        await domainsAPI.update(editingDomain.id, formData)
      } else {
        await domainsAPI.create(formData)
      }
      handleCloseDialog()
      fetchDomains()
    } catch (err) {
      console.error('Error saving domain:', err)
      setError(err.response?.data?.message || 'Failed to save domain. Please try again.')
    } finally {
      setSaving(false)
    }
  }

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this domain? This may affect pathways using this domain.')) {
      return
    }
    try {
      setError(null)
      await domainsAPI.delete(id)
      fetchDomains()
    } catch (err) {
      console.error('Error deleting domain:', err)
      setError(err.response?.data?.message || 'Failed to delete domain. It may be in use by pathways.')
    }
  }

  const handleDuplicate = async (domain) => {
    try {
      setError(null)
      await domainsAPI.duplicate(domain.id, {
        code: `${domain.code}_copy`,
        name: `${domain.name} (Copy)`,
      })
      fetchDomains()
    } catch (err) {
      console.error('Error duplicating domain:', err)
      setError(err.response?.data?.message || 'Failed to duplicate domain.')
    }
  }

  // Check if user can edit/delete a specific domain
  const canEditDomain = (domain) => {
    if (domain.is_base && !canManageBaseContent('domains')) return false
    return true
  }

  const canDeleteDomain = (domain) => {
    if (domain.is_base && !canManageBaseContent('domains')) return false
    return true
  }

  const columns = [
    {
      field: 'code',
      headerName: 'Code',
      width: 140,
      renderCell: (params) => (
        <Typography variant="body2" fontFamily="monospace" fontSize="0.75rem">
          {params.value}
        </Typography>
      ),
    },
    {
      field: 'is_base',
      headerName: 'Source',
      width: 70,
      renderCell: (params) => (
        <Typography variant="body2" color="text.secondary" fontSize="0.75rem">
          {params.value ? 'Base' : 'Custom'}
        </Typography>
      ),
    },
    {
      field: 'name',
      headerName: 'Name',
      flex: 1,
      minWidth: 180,
    },
    {
      field: 'description',
      headerName: 'Description',
      flex: 2,
      minWidth: 250,
    },
    {
      field: 'track_count',
      headerName: 'Pathways',
      width: 80,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => (
        <Typography variant="body2" fontSize="0.8125rem">
          {params.value || 0}
        </Typography>
      ),
    },
    {
      field: 'actions',
      headerName: '',
      width: 100,
      sortable: false,
      renderCell: (params) => (
        <Box display="flex" gap={0.5}>
          <Tooltip title={canEditDomain(params.row) ? 'Edit' : 'View'}>
            <IconButton size="small" onClick={() => handleOpenDialog(params.row)}>
              {canEditDomain(params.row) ? (
                <EditIcon fontSize="small" />
              ) : (
                <ViewIcon fontSize="small" />
              )}
            </IconButton>
          </Tooltip>
          <Tooltip title="Duplicate">
            <IconButton size="small" onClick={() => handleDuplicate(params.row)}>
              <DuplicateIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          {canDeleteDomain(params.row) && (
            <Tooltip title="Delete">
              <IconButton size="small" onClick={() => handleDelete(params.row.id)}>
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      ),
    },
  ]

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    )
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
        <Box>
          <Typography variant="h5">Domains</Typography>
          <Typography variant="body2" color="text.secondary">
            Manage clinical domains used to organize and sort pathways, risk codes, and other
            components throughout Asta.
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog()}
        >
          New Domain
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Paper>
        <DataGrid
          rows={domains}
          columns={columns}
          pageSize={15}
          rowsPerPageOptions={[15, 25, 50]}
          autoHeight
          disableSelectionOnClick
          getRowId={(row) => row.id}
        />
      </Paper>

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingDomain ? 'Edit Domain' : 'Create Domain'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <TextField
              fullWidth
              label="Code"
              value={formData.code}
              onChange={(e) => setFormData({ ...formData, code: e.target.value.toLowerCase().replace(/\s+/g, '_') })}
              margin="normal"
              required
              disabled={!!editingDomain}
              helperText={editingDomain ? 'Code cannot be changed after creation' : 'Unique identifier (e.g., anxiety, pain_management)'}
              inputProps={{ style: { fontFamily: 'monospace' } }}
            />
            <TextField
              fullWidth
              label="Name"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              margin="normal"
              required
              helperText="Display name for this domain"
            />
            <TextField
              fullWidth
              label="Description"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              margin="normal"
              multiline
              rows={3}
              helperText="Brief description of what this clinical domain covers"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button
            onClick={handleSave}
            variant="contained"
            disabled={saving || !formData.code || !formData.name}
          >
            {saving ? 'Saving...' : editingDomain ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}

export default Domains
